import "./PermissionPresets.scss";
import { ArrowLeftOutlined, DeleteOutlined, EditOutlined } from "@ant-design/icons";
import { Button, Checkbox, Input, Modal, Radio, Skeleton } from "antd";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useRecoilState } from "recoil";
import { env } from "../../../../env/env";
import { memberState } from "../../../../state/atoms";
import { PermissionPresetDto } from "../../../../generated/dashboard-client";
import PermissionPresetsApi from "../../../../api/PermissionPresetsApi";
import { success } from "../../../../service/MessageService";
import { handleServerError } from "../../../../util/ErrorHandler";
import ControlIcon from "../../../icons/ControlIcon";
import {
  ADD_ONS,
  AccessLevel,
  AddOn,
  accessLevelsFor,
  addOnNote,
  addOnsFor,
  clearInvalidAddOns,
  isAddOnChecked,
  isAddOnDisabled,
  levelLabel,
  permissionSummary,
} from "./permissionModel";

interface Preset {
  id: string;
  name: string;
  level: AccessLevel | null;
  addOns: AddOn[];
  isDefault: boolean;
}

// The DTO stores one boolean per permission. The abstract level maps to the three base
// flags; add-ons map to createTemplates / publish / orderPrint.
function fromDto(dto: PermissionPresetDto): Preset {
  const level: AccessLevel | null = dto.viewOnly
    ? "viewOnly"
    : dto.dataOnly
      ? "editMenuData"
      : dto.manageDesigns
        ? "editDesigns"
        : null;
  const addOns = ADD_ONS.filter((a) => {
    if (a.key === "createTemplates") return !!dto.createTemplates;
    if (a.key === "publish") return !!dto.publish;
    if (a.key === "orderPrint") return !!dto.orderPrint;
    return !!dto.holidayTemplates;
  }).map((a) => a.key);
  return { id: dto.id ?? "", name: dto.name ?? "", level, addOns, isDefault: !!dto.default };
}

function toDto(name: string, level: AccessLevel | null, addOns: AddOn[], isDefault: boolean): PermissionPresetDto {
  return {
    name,
    default: isDefault,
    viewOnly: level === "viewOnly",
    dataOnly: level === "editMenuData",
    manageDesigns: level === "editDesigns",
    createTemplates: addOns.includes("createTemplates"),
    publish: addOns.includes("publish"),
    orderPrint: addOns.includes("orderPrint"),
    holidayTemplates: addOns.includes("holidayTemplates"),
  };
}

export function PermissionPresets() {
  const navigate = useNavigate();
  const [member] = useRecoilState(memberState);
  const teamId = member.teamId ?? "";
  const showItemLibrary = member.showItemLibrary ?? false;
  const availableAddOns = addOnsFor(!!member.diningRDIntegration);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [makingDefaultId, setMakingDefaultId] = useState<string | null>(null);
  const [presets, setPresets] = useState<Preset[]>([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [name, setName] = useState("");
  const [level, setLevel] = useState<AccessLevel | null>(null);
  const [addOns, setAddOns] = useState<AddOn[]>([]);

  const fetchPresets = useCallback(() => {
    if (!teamId) return;
    setLoading(true);
    PermissionPresetsApi.getPresets(teamId)
      .then(resp => setPresets(resp.data.map(fromDto)))
      .catch(handleServerError)
      .finally(() => setLoading(false));
  }, [teamId]);

  useEffect(() => {
    fetchPresets();
  }, [fetchPresets]);

  function openNew() {
    setEditingId(null);
    setName("");
    setLevel(null);
    setAddOns([]);
    setModalOpen(true);
  }

  function openEdit(preset: Preset) {
    setEditingId(preset.id);
    setName(preset.name);
    setLevel(preset.level);
    setAddOns(preset.addOns);
    setModalOpen(true);
  }

  function onLevelChange(next: AccessLevel) {
    setLevel(next);
    setAddOns((prev) => clearInvalidAddOns(next, prev));
  }

  function toggleAddOn(key: AddOn, checked: boolean) {
    setAddOns((prev) => {
      const next = new Set(prev);
      if (checked) next.add(key);
      else next.delete(key);
      return ADD_ONS.filter((a) => next.has(a.key)).map((a) => a.key);
    });
  }

  function onSave() {
    const trimmed = name.trim();
    if (!trimmed || !level || !teamId) return;

    const editing = editingId ? presets.find(p => p.id === editingId) : null;
    const dto = toDto(trimmed, level, addOns, editing?.isDefault ?? false);

    setSaving(true);
    const request = editingId
      ? PermissionPresetsApi.updatePreset(teamId, editingId, dto)
      : PermissionPresetsApi.createPreset(teamId, dto);

    request
      .then(() => {
        success(editingId ? "Preset updated" : "Preset created");
        setModalOpen(false);
        fetchPresets();
      })
      .catch(handleServerError)
      .finally(() => setSaving(false));
  }

  function onMakeDefault(preset: Preset) {
    if (!teamId) return;
    const dto = toDto(preset.name, preset.level, preset.addOns, true);
    setMakingDefaultId(preset.id);
    PermissionPresetsApi.updatePreset(teamId, preset.id, dto)
      .then(() => {
        success("Default preset updated");
        // The backend enforces a single default per team, so mirror that locally:
        // mark this preset default and clear the rest, without refetching the list.
        setPresets(prev => prev.map(p => ({ ...p, isDefault: p.id === preset.id })));
      })
      .catch(handleServerError)
      .finally(() => setMakingDefaultId(null));
  }

  function onDelete(preset: Preset) {
    Modal.confirm({
      title: "Delete preset?",
      content: `Are you sure you want to delete "${preset.name}"? This can't be undone.`,
      okText: "Delete",
      okButtonProps: { danger: true },
      onOk() {
        if (!teamId) return;
        return PermissionPresetsApi.deletePreset(teamId, preset.id)
          .then(() => {
            success("Preset deleted");
            fetchPresets();
          })
          .catch(handleServerError);
      },
    });
  }

  return (
    <div className={"content permission-presets"}>
      <a className={"back"} onClick={() => navigate(env.dashboardPath + "/account/team")}>
        <ArrowLeftOutlined /> Back to team
      </a>

      <header>
        <div className={"heading"}>
          <h3>Permission Presets</h3>
          <p>Reusable bundles you can reference in CSV imports or apply when adding users manually.</p>
        </div>
        {!loading && presets.length > 0 &&
          <Button type={"primary"} size={"large"} className={"new"} onClick={openNew}>+ New preset</Button>}
      </header>

      {loading && <Skeleton />}

      {!loading && presets.length === 0 &&
        <div className={"empty-state"}>
          <div className={"empty-icon"}><ControlIcon /></div>
          <h4>No presets yet</h4>
          <p>Create your first preset to get started.</p>
          <Button type={"primary"} size={"large"} className={"new"} onClick={openNew}>+ New Preset</Button>
        </div>}

      {!loading && presets.length > 0 &&
        <div className={"preset-list"}>
          {presets.map(preset => (
            <div className={`preset-card${preset.isDefault ? " default" : ""}`} key={preset.id}>
              <div className={"info"}>
                <div className={"name"}>{preset.name}</div>
                <div className={"summary"}>{permissionSummary(preset.level, preset.addOns, showItemLibrary)}</div>
                <div className={"pills"}>
                  <span className={"badge"}>{levelLabel(preset.level, showItemLibrary)}</span>
                  {availableAddOns.filter((a) => isAddOnChecked(preset.level, a, preset.addOns)).map((a) => (
                    <span className={"pill"} key={a.key}>{a.chip}</span>
                  ))}
                </div>
              </div>
              <div className={"actions"}>
                {preset.isDefault
                  ? <span className={"default-label"}>Default</span>
                  : <Button className={"make-default"} loading={makingDefaultId === preset.id} onClick={() => onMakeDefault(preset)}>Make default</Button>}
                <button className={"icon-btn"} onClick={() => openEdit(preset)} aria-label={"Edit preset"}>
                  <EditOutlined />
                </button>
                <button
                  className={"icon-btn"}
                  onClick={() => onDelete(preset)}
                  disabled={preset.isDefault}
                  aria-label={"Delete preset"}
                >
                  <DeleteOutlined />
                </button>
              </div>
            </div>
          ))}
        </div>}

      <Modal
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        footer={null}
        width={760}
        className={"preset-modal"}
      >
        <h4>{editingId ? "Edit Preset" : "New Preset"}</h4>
        <p className={"subtitle"}>Select permissions for this preset. Rules are enforced automatically.</p>

        <Input
          className={"name-input"}
          placeholder={"Preset name"}
          value={name}
          onChange={ev => setName(ev.target.value)}
        />

        <div className={"perm-groups"}>
          <div className={"perm-col access-level"}>
            <div className={"group-title"}>ACCESS LEVEL</div>
            <Radio.Group value={level ?? undefined} onChange={(ev) => onLevelChange(ev.target.value)}>
              {accessLevelsFor(showItemLibrary).map((lvl) => (
                <Radio key={lvl.key} value={lvl.key} className={`level-card${level === lvl.key ? " selected" : ""}`}>
                  <span className={"label"}>{lvl.label}</span>
                  <span className={"desc"}>{lvl.description}</span>
                </Radio>
              ))}
            </Radio.Group>
          </div>

          <div className={"perm-col add-ons"}>
            <div className={"group-title"}>ADD ONS</div>
            {availableAddOns.map((addOn) => {
              const disabled = isAddOnDisabled(level, addOn);
              const note = addOnNote(level, addOn);
              return (
                <label className={`addon-card${disabled ? " disabled" : ""}`} key={addOn.key}>
                  <Checkbox
                    checked={isAddOnChecked(level, addOn, addOns)}
                    disabled={disabled}
                    onChange={ev => toggleAddOn(addOn.key, ev.target.checked)}
                  />
                  <span className={"text"}>
                    <span className={"label"}>{addOn.label}</span>
                    <span className={"desc"}>{addOn.description}</span>
                    {note && <span className={"note"}>{note}</span>}
                  </span>
                </label>
              );
            })}
          </div>
        </div>

        <div className={"perm-summary"}>
          <span className={"muted"}>This user can: </span>
          <b>{permissionSummary(level, addOns, showItemLibrary)}</b>
        </div>

        <div className={"modal-actions"}>
          <Button className={"cancel"} onClick={() => setModalOpen(false)}>Cancel</Button>
          <Button type={"primary"} className={"save"} loading={saving} onClick={onSave} disabled={!name.trim() || !level}>
            Save Preset
          </Button>
        </div>
      </Modal>
    </div>
  );
}
